import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Checkbox, Empty, Input, Pagination, Select, Skeleton, Tooltip, message } from 'antd'
import moment from 'moment'
import { FaChevronDown, FaChevronUp } from 'react-icons/fa'
import { Bus, Route, Gauge, Milestone, Target, Filter, RefreshCw, Search, Activity, Users, Building2 } from 'lucide-react'
import { Datepicker, LineVehiclesDrawer, RouteHistoryMap, SearchOrganizations } from '../../Components'
import { secureApi } from '../../Config/api'
import { GET } from '../../utils/apis'
import { translations } from '../../utils/constants'
import { verifyToken, fetchAllOrganization } from '../../utils/helpers'

const VEHICLES_PER_PAGE = 10
// Trips shown per vehicle before the "show all" button; a full day can be
// 25+ runs, which would push the next vehicle far down the page.
const TRIPS_PREVIEW = 8

const STATUS_COLORS = {
    normal: '#52c41a',
    abnormal: '#ff4d4f',
    no_schedule: '#8c8c8c',
}

const fmtTime = (iso) => (iso ? moment(iso).format('HH:mm') : '-')
const pctOf = (ratio) => (Number.isFinite(ratio) ? `${Math.round(ratio * 100)}%` : '-')

// One journey (trip) of a vehicle: header line, metrics, and — once opened —
// the map with the road-snapped trace and the matched trip's geometry.
const JourneyCard = ({ journey, detail, detailLoading, expanded, onToggle, t }) => {
    const status = journey?.status || 'no_schedule'
    const statusLabel = { normal: t.normal, abnormal: t.abnormal, no_schedule: t.noSchedule }[status]
    return (
        <div className={`rh-journey rh-journey-${status}`}>
            <div className='rh-journey-head' onClick={onToggle} role='button'>
                {journey?.trip?.line ? <span className='rh-line-badge'>{journey.trip.line}</span> : null}
                <span className='rh-journey-time'>
                    {fmtTime(journey?.start_time)} - {fmtTime(journey?.end_time)}
                </span>
                <span className='rh-journey-headsign'>
                    {journey?.trip?.headsign || ''}
                    {journey?.trip?.starts ? ` (${journey.trip.starts} - ${journey.trip.ends})` : ''}
                </span>
                <span className={`rh-status-pill rh-status-${status}`}>{statusLabel}</span>
                {expanded ? <FaChevronUp className='rh-chevron' /> : <FaChevronDown className='rh-chevron' />}
            </div>
            <div className='rh-journey-metrics'>
                <span>
                    <strong>{t.kmTraveled}:</strong> {journey?.km_traveled ?? '-'} km
                    {journey?.scheduled_km != null && (
                        <span className='rh-muted'>
                            {' vs '}{journey.scheduled_km} km ({t.scheduledDistance?.toLowerCase()})
                        </span>
                    )}
                </span>
                <span><strong>{t.avgSpeed}:</strong> {journey?.avg_speed_kmh ?? '-'} km/h</span>
                <span><strong>{t.maxSpeed}:</strong> {journey?.max_speed_kmh ?? '-'} km/h</span>
                {journey?.adherence != null && (
                    <span><strong>{t.adherence}:</strong> {pctOf(journey.adherence)}</span>
                )}
                {journey?.stops_count > 0 && (
                    <span><strong>{t.stops}:</strong> {journey.stops_count}</span>
                )}
                <span>
                    <strong>{t.totalPassengers}:</strong> {journey?.passengers ?? 0}
                </span>
            </div>
            {expanded && (
                <div className='rh-journey-map'>
                    {detailLoading || !detail ? (
                        <Skeleton active paragraph={{ rows: 4 }} />
                    ) : (
                        <RouteHistoryMap
                            trace={detail?.trace_normalized?.length > 1 ? detail.trace_normalized : detail?.trace}
                            shape={detail?.shape}
                            stops={detail?.stops}
                        />
                    )}
                </div>
            )}
        </div>
    )
}

const RouteHistory = ({ language }) => {
    const t = translations[language] || {}
    const dispatch = useDispatch()
    const history = useHistory()
    const user = useSelector((state) => state?.authReducer?.user)
    const organizationDetails = useSelector((state) => state?.authReducer?.organization)
    const allOrganizations = useSelector((state) => state?.organizationReducer?.allOrganizations) || []
    const isSuperAdmin = user?.role === 'superAdmin'

    const [selectedDate, setSelectedDate] = useState(new Date())
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    // Line whose vehicles are open in the right-side drawer.
    const [openLine, setOpenLine] = useState(null)
    const [refreshing, setRefreshing] = useState(false)
    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState('all')
    // superAdmin: organisation ids picked in the shared filter panel (same
    // panel as Vehicles / Devices / GTFS); empty = whole fleet.
    const [organizationFilter, setOrganizationFilter] = useState([])
    const [searchModal, setSearchModal] = useState(false)
    const [onlyWithTrips, setOnlyWithTrips] = useState(true)
    const [page, setPage] = useState(1)
    const [openVehicles, setOpenVehicles] = useState({})
    const [showAllTrips, setShowAllTrips] = useState({}) // vehicleId -> true
    const [openJourney, setOpenJourney] = useState(null) // `${vehicleId}|${start_time}`
    // Full journeys (trace/shape/stops) per vehicle, loaded when a journey
    // is expanded — the fleet call only ships summaries.
    const [details, setDetails] = useState({})
    const [detailLoading, setDetailLoading] = useState({})
    const requestRef = useRef(0)

    useEffect(() => {
        verifyToken(history, dispatch)
        // superAdmin filters the fleet by organisation (server-side).
        if (isSuperAdmin) fetchAllOrganization({ dispatch })
    }, [])

    const fetchFleet = async ({ silent = false } = {}) => {
        const token = ++requestRef.current
        if (!silent) setLoading(true)
        else setRefreshing(true)
        try {
            const response = await secureApi.get(GET?.GET_FLEET_ROUTE_HISTORY, {
                params: {
                    date: moment(selectedDate).format('YYYY-MM-DD'),
                    user_id: user?.id,
                    organization_id: organizationDetails?.id,
                    // superAdmin only — narrows the whole-fleet response to one organisation.
                    ...(isSuperAdmin && organizationFilter.length
                        ? { filter_organization_id: organizationFilter.join(',') }
                        : {}),
                },
            })
            if (token !== requestRef.current) return
            if (response?.success) {
                setData(response?.data || null)
            } else {
                setData(null)
                if (!silent) message.error(response?.message || t.noRoutesFound)
            }
        } catch (error) {
            if (token !== requestRef.current) return
            setData(null)
            if (!silent) message.error(t.noRoutesFound)
        } finally {
            if (token === requestRef.current) {
                setLoading(false)
                setRefreshing(false)
            }
        }
    }

    // Reload on day change; journeys still being road-matched in the
    // background are re-fetched a few times without flashing the skeleton.
    useEffect(() => {
        setPage(1)
        setOpenVehicles({})
        setShowAllTrips({})
        setOpenJourney(null)
        setDetails({})
        fetchFleet()
    }, [selectedDate, user?.id, organizationDetails?.id, organizationFilter])

    const handleOrganizationChange = (ids) => {
        setOrganizationFilter(Array.isArray(ids) ? ids : [])
        setPage(1)
    }

    // While the server is still building the day (`building`) poll faster;
    // journeys being road-matched afterwards re-fetch at the calmer pace.
    useEffect(() => {
        if (!data?.normalizing) return
        const timer = setTimeout(() => fetchFleet({ silent: true }), data?.building ? 4000 : 8000)
        return () => clearTimeout(timer)
    }, [data])

    const stats = data?.stats
    const vehicles = data?.vehicles || []
    // A day still being built server-side shows the skeleton, not "no routes".
    const busy = loading || !!data?.building


    const filteredVehicles = useMemo(() => {
        const q = search.trim().toLowerCase()
        return vehicles
            .map((v) => {
                const journeys = statusFilter === 'all'
                    ? v.journeys
                    : (v.journeys || []).filter((j) => j.status === statusFilter)
                return { ...v, filteredJourneys: journeys }
            })
            .filter((v) => {
                if (onlyWithTrips && !(v.filteredJourneys || []).length) return false
                if (statusFilter !== 'all' && !(v.filteredJourneys || []).length) return false
                if (!q) return true
                const hay = [
                    v.vehicle?.vehicle_name,
                    v.vehicle?.registering_number,
                    v.vehicle?.license_plate,
                    v.vehicle?.vehicle_model,
                    v.vehicle?.organization_name,
                    v.vehicle?.serial_number,
                    ...(v.journeys || []).map((j) => j?.trip?.line),
                    ...(v.journeys || []).map((j) => j?.trip?.headsign),
                ].filter(Boolean).join(' ').toLowerCase()
                return hay.includes(q)
            })
    }, [vehicles, search, statusFilter, onlyWithTrips])

    const pages = Math.max(1, Math.ceil(filteredVehicles.length / VEHICLES_PER_PAGE))
    const currentPage = Math.min(page, pages)
    const pagedVehicles = filteredVehicles.slice((currentPage - 1) * VEHICLES_PER_PAGE, currentPage * VEHICLES_PER_PAGE)

    // A short list opens by itself; long lists start collapsed.
    const isVehicleOpen = (id) => (id in openVehicles ? openVehicles[id] : filteredVehicles.length <= 3)

    const loadDetail = async (vehicleId) => {
        if (details[vehicleId] || detailLoading[vehicleId]) return
        setDetailLoading((s) => ({ ...s, [vehicleId]: true }))
        try {
            const response = await secureApi.get(GET?.GET_VEHICLE_ROUTE_HISTORY, {
                params: { vehicle_id: vehicleId, date: moment(selectedDate).format('YYYY-MM-DD') },
            })
            const journeys = response?.success ? response?.data?.journeys || [] : []
            const byStart = {}
            journeys.forEach((j) => { byStart[j.start_time] = j })
            setDetails((s) => ({ ...s, [vehicleId]: byStart }))
        } catch (error) {
            setDetails((s) => ({ ...s, [vehicleId]: {} }))
        } finally {
            setDetailLoading((s) => ({ ...s, [vehicleId]: false }))
        }
    }

    const toggleJourney = (vehicleId, journey) => {
        const key = `${vehicleId}|${journey.start_time}`
        if (openJourney === key) {
            setOpenJourney(null)
            return
        }
        setOpenJourney(key)
        loadDetail(vehicleId)
    }

    const kpi = (label, value, sub, color, Icon) => (
        <div className='dash-kpi-card rh-kpi' style={{ '--kpi-color': color }} key={label}>
            <div className='dash-kpi-main'>
                <div className='dash-kpi-icon' style={{ backgroundColor: `${color}1A`, color }}>
                    <Icon size={22} />
                </div>
                <div className='dash-kpi-info'>
                    <p className='dash-kpi-count'>{value ?? '-'}</p>
                    <p className='dash-kpi-label'>{label}</p>
                    {sub ? <p className='dash-kpi-sub'>{sub}</p> : null}
                </div>
            </div>
        </div>
    )

    const dateLabel = moment(selectedDate).format('DD/MM/YYYY')

    return (
        <div className='main-container'>
            <div className='main-inner-container'>
                <div className='insider-container rp-page rh-page'>
                    <div className='insider-container-buttons rp-header'>
                        <div>
                            <h1 className='main-heading2 margin-bottom-0'>{t.routeHistory}</h1>
                            <p className='rp-header-sub'>{t.routeHistorySubtitle}</p>
                        </div>
                        <div className='rh-header-actions'>
                            {isSuperAdmin && (
                                <div className='rh-org-anchor'>
                                    <button
                                        type='button'
                                        className={`add-button organization ${searchModal ? 'open-organization' : 'close-organization'}`}
                                        // The panel closes itself on any outside mousedown; without
                                        // this the button's click would then re-open it.
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onClick={() => setSearchModal((open) => !open)}
                                    >
                                        <Building2 />
                                        {t.organizations}
                                        {organizationFilter.length ? (
                                            <div className='filter-count-badge'>{organizationFilter.length}</div>
                                        ) : null}
                                    </button>
                                    {/* The shared panel is fixed to the viewport on the
                                        list pages; here it drops down under its button. */}
                                    <div className={`search-organization-main-div ${searchModal ? 'open' : ''}`}>
                                        <SearchOrganizations
                                            selectedOrganizations={organizationFilter}
                                            handleOrganizationChange={handleOrganizationChange}
                                            organizations={allOrganizations}
                                            setSearchModal={setSearchModal}
                                            selectedFilter={organizationFilter}
                                            searchModal={searchModal}
                                            language={language}
                                            searchType='vehicles'
                                        />
                                    </div>
                                </div>
                            )}
                            <Datepicker value={selectedDate} language={language} onChange={(d) => d && setSelectedDate(d.toDate ? d.toDate() : d)} />
                            <Tooltip title={t.refreshButton}>
                                <Button
                                    className='rh-refresh-btn'
                                    onClick={() => fetchFleet({ silent: true })}
                                    loading={refreshing}
                                    icon={<RefreshCw size={16} />}
                                />
                            </Tooltip>
                        </div>
                    </div>

                    {busy ? (
                        <div className='rh-kpis'>
                            {[0, 1, 2, 3, 4, 5].map((i) => (
                                <div className='dash-kpi-card rh-kpi' key={i}>
                                    <Skeleton active avatar paragraph={{ rows: 1 }} />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className='rh-kpis'>
                            {kpi(
                                t.vehiclesWithTrips,
                                stats?.vehicles_with_trips ?? 0,
                                `${stats?.vehicles_total ?? 0} ${t.vehiclesWithRouter}`,
                                '#40AFFF',
                                Bus
                            )}
                            {kpi(
                                t.totalTrips,
                                stats?.trips ?? 0,
                                `${stats?.normal ?? 0} ${t.onSchedule} · ${stats?.abnormal ?? 0} ${t.offSchedule}`,
                                '#ED2328',
                                Route
                            )}
                            {kpi(
                                t.kmTraveled,
                                `${stats?.km_traveled ?? 0} km`,
                                stats?.scheduled_km ? `${t.scheduledDistance}: ${stats.scheduled_km} km` : null,
                                '#884DFF',
                                Milestone
                            )}
                            {kpi(
                                t.avgSpeed,
                                stats?.avg_speed_kmh != null ? `${stats.avg_speed_kmh} km/h` : '-',
                                stats?.max_speed_kmh != null ? `${t.maxSpeed}: ${stats.max_speed_kmh} km/h` : null,
                                '#F59E0B',
                                Gauge
                            )}
                            {kpi(
                                t.avgAdherence,
                                stats?.avg_adherence != null ? pctOf(stats.avg_adherence) : '-',
                                stats?.on_schedule_pct != null ? `${stats.on_schedule_pct}% ${t.onSchedule?.toLowerCase()}` : null,
                                '#00A86B',
                                Target
                            )}
                            {kpi(
                                t.totalPassengers,
                                stats?.passengers ?? 0,
                                `${stats?.trips ?? 0} ${t.trips?.toLowerCase()}`,
                                '#9B59B6',
                                Users
                            )}
                        </div>
                    )}

                    {!busy && data?.lines?.length > 0 && (
                        <div className='vd-card rh-lines-card'>
                            <div className='vd-card-header'>
                                <h2 className='vd-card-title'>
                                    <Activity size={16} className='rp-title-icon' />
                                    {t.tripsPerLine}
                                </h2>
                                <span className='vd-card-meta'>{data.lines.length} {t.routes}</span>
                            </div>
                            <div className='vd-card-body'>
                                <div className='dash-lines rh-lines'>
                                    {(() => {
                                        const max = Math.max(...data.lines.map((l) => l.trips || 0), 1)
                                        return data.lines.slice(0, 12).map((l) => (
                                            <div className='dash-line-row dash-line-row-click' key={l.line} onClick={() => setOpenLine(l)} title={t.lineVehicles}>
                                                <span className='dash-line-badge'>{l.line}</span>
                                                <div className='dash-line-track rh-line-track'>
                                                    <span className='rh-line-normal' style={{ width: `${(l.normal / max) * 100}%` }} />
                                                    <span className='rh-line-abnormal' style={{ width: `${(l.abnormal / max) * 100}%` }} />
                                                </div>
                                                <span className='dash-line-count'>
                                                    {l.trips}
                                                    <span className='rh-line-km'>{l.km_traveled} km</span>
                                                    <span className='dash-line-vehicles'><Bus size={11} /> {l.vehicles_count ?? 0}</span>
                                                </span>
                                            </div>
                                        ))
                                    })()}
                                </div>
                                <div className='dash-legend rh-legend'>
                                    <span><span className='dash-legend-dot' style={{ background: STATUS_COLORS.normal }} />{t.normal}</span>
                                    <span><span className='dash-legend-dot' style={{ background: STATUS_COLORS.abnormal }} />{t.abnormal}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    <LineVehiclesDrawer line={openLine} open={!!openLine} onClose={() => setOpenLine(null)} language={language} />

                    <div className='vd-card rp-table-card'>
                        {/* Search and filters live in the list header so they stay
                            next to the vehicles they act on (the page is long). */}
                        <div className='vd-card-header rh-list-header'>
                            <div className='vl-list-title'>
                                <h2 className='vd-card-title'>
                                    <Bus size={16} className='rp-title-icon' />
                                    {t.vehicles}
                                </h2>
                                <span className='vl-total-chip'>
                                    {t.totalRecord}: <strong>{filteredVehicles.length}</strong>
                                </span>
                                <span className='vd-card-meta'>
                                    {dateLabel}
                                    {data?.normalizing ? ` · ${t.mapMatchingInProgress}` : ''}
                                </span>
                            </div>
                            <div className='rh-list-tools'>
                                <Input
                                    allowClear
                                    prefix={<Search size={15} className='rh-search-icon' />}
                                    placeholder={t.searchVehicle}
                                    value={search}
                                    onChange={(e) => { setSearch(e.target.value); setPage(1) }}
                                    className='rh-filter-input'
                                    aria-label={t.searchVehicle}
                                />
                                <Select
                                    value={statusFilter}
                                    onChange={(v) => { setStatusFilter(v); setPage(1) }}
                                    className='rh-filter-select'
                                    suffixIcon={<Filter size={14} />}
                                    options={[
                                        { value: 'all', label: t.allStatuses },
                                        { value: 'normal', label: t.normal },
                                        { value: 'abnormal', label: t.abnormal },
                                        { value: 'no_schedule', label: t.noSchedule },
                                    ]}
                                />
                                <Checkbox
                                    className='rh-only-trips'
                                    checked={onlyWithTrips}
                                    onChange={(e) => { setOnlyWithTrips(e.target.checked); setPage(1) }}
                                >
                                    {t.onlyVehiclesWithTrips}
                                </Checkbox>
                            </div>
                        </div>
                        <div className='vd-card-body'>
                            {busy ? (
                                <Skeleton active paragraph={{ rows: 8 }} />
                            ) : !filteredVehicles.length ? (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={
                                        <div className='rp-empty'>
                                            <p className='rp-empty-title'>{t.noTripsForDay}</p>
                                            <p className='rp-empty-hint'>{t.noTripsHint}</p>
                                        </div>
                                    }
                                />
                            ) : (
                                <>
                                    {pagedVehicles.map((v) => {
                                        const id = v.vehicle?.id
                                        const open = isVehicleOpen(id)
                                        const totals = v.totals || {}
                                        return (
                                            <div className={`rh-vehicle ${open ? 'rh-vehicle-open' : ''}`} key={id}>
                                                <div
                                                    className='rh-vehicle-head'
                                                    role='button'
                                                    onClick={() => setOpenVehicles((s) => ({ ...s, [id]: !open }))}
                                                >
                                                    <div className='rh-vehicle-id'>
                                                        <span className='rh-vehicle-icon'><Bus size={18} /></span>
                                                        <div className='rh-vehicle-names'>
                                                            <span className='rh-vehicle-name capitalized-name'>{v.vehicle?.vehicle_name || '-'}</span>
                                                            <span className='rh-vehicle-sub'>
                                                                {[v.vehicle?.registering_number, v.vehicle?.vehicle_model, isSuperAdmin ? v.vehicle?.organization_name : null]
                                                                    .filter(Boolean).join(' · ')}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className='rh-vehicle-stats'>
                                                        <span className='rh-chip'><strong>{totals.trips ?? 0}</strong> {t.trips}</span>
                                                        <span className='rh-chip'><strong>{totals.km_traveled ?? 0}</strong> km</span>
                                                        <span className='rh-chip'><strong>{totals.passengers ?? 0}</strong> {t.passengersTxt?.toLowerCase()}</span>
                                                        {totals.avg_speed_kmh != null && (
                                                            <span className='rh-chip'><strong>{totals.avg_speed_kmh}</strong> km/h</span>
                                                        )}
                                                        {totals.avg_adherence != null && (
                                                            <span className='rh-chip'><strong>{pctOf(totals.avg_adherence)}</strong> {t.adherence?.toLowerCase()}</span>
                                                        )}
                                                        {totals.first_start && (
                                                            <span className='rh-chip rh-chip-time'>
                                                                {fmtTime(totals.first_start)} → {fmtTime(totals.last_end)}
                                                            </span>
                                                        )}
                                                        {totals.normal > 0 && <span className='rh-status-pill rh-status-normal'>{totals.normal} {t.normal}</span>}
                                                        {totals.abnormal > 0 && <span className='rh-status-pill rh-status-abnormal'>{totals.abnormal} {t.abnormal}</span>}
                                                        {totals.no_schedule > 0 && <span className='rh-status-pill rh-status-no_schedule'>{totals.no_schedule} {t.noSchedule}</span>}
                                                        {open ? <FaChevronUp className='rh-chevron' /> : <FaChevronDown className='rh-chevron' />}
                                                    </div>
                                                </div>
                                                {open && (
                                                    <div className='rh-vehicle-body'>
                                                        {!(v.filteredJourneys || []).length ? (
                                                            <div className='rh-vehicle-empty'>{v.error || t.noTripsForVehicle}</div>
                                                        ) : (() => {
                                                            const all = v.filteredJourneys
                                                            const expandedList = !!showAllTrips[id]
                                                            const visible = expandedList ? all : all.slice(0, TRIPS_PREVIEW)
                                                            const hidden = all.length - visible.length
                                                            return (
                                                                <>
                                                                    {visible.map((j) => (
                                                                        <JourneyCard
                                                                            key={`${id}-${j.start_time}`}
                                                                            journey={j}
                                                                            t={t}
                                                                            expanded={openJourney === `${id}|${j.start_time}`}
                                                                            onToggle={() => toggleJourney(id, j)}
                                                                            detail={details[id]?.[j.start_time]}
                                                                            detailLoading={!!detailLoading[id]}
                                                                        />
                                                                    ))}
                                                                    {all.length > TRIPS_PREVIEW && (
                                                                        <div className='rh-show-more'>
                                                                            <Button
                                                                                type='link'
                                                                                className='rh-show-more-btn'
                                                                                onClick={() => {
                                                                                    setShowAllTrips((s) => ({ ...s, [id]: !expandedList }))
                                                                                    setOpenJourney(null)
                                                                                }}
                                                                            >
                                                                                {expandedList
                                                                                    ? t.showFewerTrips
                                                                                    : `${t.showAllTrips} (${hidden} ${t.more})`}
                                                                                {expandedList ? <FaChevronUp className='rh-chevron' /> : <FaChevronDown className='rh-chevron' />}
                                                                            </Button>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )
                                                        })()}
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    })}
                                    {filteredVehicles.length > VEHICLES_PER_PAGE && (
                                        <div className='rh-pagination'>
                                            <Pagination
                                                size='small'
                                                current={currentPage}
                                                pageSize={VEHICLES_PER_PAGE}
                                                total={filteredVehicles.length}
                                                showSizeChanger={false}
                                                onChange={(p) => { setPage(p); setOpenJourney(null) }}
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default RouteHistory
